import React, { useEffect, useState } from 'react'
import DashboardLayout from '../../components/dashboardLayout'
import Container from '../../elements/Container'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import ExperimentDetails from '../ExperimentDetails'
import { useExperimentApi } from '../../store/api/hooks/experimentApiHook'
import { SuccessApiResponse } from '../../store/api/apiTypes'
import { URLS } from '../../_config'
import toast from 'react-hot-toast'
import { trackEvent } from '../../_common/functions'
import ExperimentHeaderBar from './components/ExperimentHeaderBar'
import Loader from '../../components/loader'
import { EXPERIMENT_TYPES } from '../../_common/constant'

const Insight: React.FC = () => {
    const { t } = useTranslation()
    const routeParams = useParams()
    const experimentHitLiteral: any = routeParams['exptLiteral']
    const navigate = useNavigate()
    const { getExperimentDetails, getExperimentsAlt, getCardData, getDMLCardData, getMTECardData, getExperimentInsideMethodology } = useExperimentApi()
    const [experimentDetailLoadingStatus, setExperimentDetailLoadingStatus] = useState<string>('Loading experiment details...')
    const [loading, setLoading] = useState<boolean>(true)
    const [parentExperimentLiteral, setParentExperimentLiteral] = useState<string | null>(null)
    const [activeExperimentLiteral, setActiveExperimentLiteral] = useState<string | null>(null)
    const [experiment, setExperiment] = useState<any>(null)
    const [experiments, setExperiments] = useState<any>(null)
    const [mteCardData, setmteCardData] = useState<any>(null)
    const [dmlCardData, setdmlCardData] = useState<any>(null)

    const getExperimentDetailsData = (literal: string): Promise<any> => {
        return new Promise((resolve, reject) => {
            getExperimentDetails(
                { literal, use_orm: true },
                (resp: SuccessApiResponse<any>) => {
                    if (resp.data.is_recurring_child_experiment || resp.data.schedule_parent_experiment) {
                        resolve(resp.data)
                    } else {
                        const isInvalid = ![200, 300, 401, 402].includes(Number.parseInt(resp.data.status))
                        if (isInvalid) {
                            reject(new Error('Invalid experiment status'))
                        }
                        resolve(resp.data)
                    }
                },
                (error: any) => {
                    if (error.status === 404) {
                        reject(new Error("Oops! Sorry, the experiment that you are looking for is either deleted or doesn't exist"))
                    } else {
                        reject(new Error(error?.message))
                    }
                },
            )
        })
    }

    const getChildExperiments = (parentLiteral: any, experimentHitData: any) => {
        setExperimentDetailLoadingStatus('fetching experiment schedules and childs experiment details')
        setParentExperimentLiteral(parentLiteral)
        getExperimentsAlt(
            {
                experiment_parent_literal: parentLiteral,
                per_page: 1000,
                page: 1,
                is_recurring_child_experiment: true,
            },
            async (resp: any) => {
                const _experiments: any = { childs: {} }
                resp.data.result.forEach((experiment: any) => {
                    _experiments.childs[experiment.experiment_literal] = experiment
                })
                if (parentLiteral == experimentHitLiteral) {
                    _experiments[parentLiteral] = experimentHitData
                } else {
                    _experiments[parentLiteral] = await getExperimentDetailsData(parentLiteral)
                }
                trackEvent('insight_child_experiments_loaded', {
                    experiment_literal: parentLiteral,
                    child_count: resp.data.result.length,
                })
                setExperiments(_experiments)
                void preloadData(_experiments)
            },
            (error: any) => {
                toast.error(error.message ?? error)
            },
        )
    }

    const getExperimentALLData = async (literal: string): Promise<any> => {
        try {
            const experimentHitData = await getExperimentDetailsData(literal)
            setExperiment(experimentHitData)
            setExperimentDetailLoadingStatus('checking experiment data')
            if (experimentHitData?.schedule_parent_experiment) {
                getChildExperiments(experimentHitLiteral, experimentHitData)
            } else if (experimentHitData?.is_recurring_child_experiment) {
                getChildExperiments(experimentHitData?.recurring_parent_literal, experimentHitData)
            } else {
                setExperimentDetailLoadingStatus('No schedules or child experiments found, showing insights for the current experiment')
                const exp = await Promise.all([getDataBeforeLoad(experimentHitData)])
                setmteCardData(exp[0]?.insight?.kpiCombinations[0]?.mteCardDetails)
                setdmlCardData(exp[0]?.insight?.kpiCombinations[0]?.dmlCardDetails)
                setLoading(false)
                setActiveExperimentLiteral(experimentHitLiteral)
            }
        } catch (error: any) {
            trackEvent('insight_experiment_load_failed', {
                experiment_literal: experimentHitLiteral,
                error_message: error?.message ?? 'Something went wrong',
                status_code: error?.status ?? null,
            })
            toast.error(error?.message ?? t('Something went wrong'))
            void navigate(URLS.MY_EXPERIMENTS)
        }
    }

    const getMethodologyByKPI = (literal: string, group: string, kpi: string, is_mte: boolean): Promise<any> => {
        return new Promise((resolve, reject) => {
            const payload: any = { literal, group: group || 'Test', kpi, is_mte }
            getExperimentInsideMethodology(
                payload,
                (resp: any) => {
                    if (resp.data.length > 1 && resp.data[0]?.category == 'did') {
                        const config: any = { label: resp.data[1]?.category, value: resp.data[1]?.category, blocking_factor_methodology: resp.data[1]?.category_blocking_factor }
                        resolve(config.value)
                    } else {
                        const config: any = { label: resp.data[0]?.category, value: resp.data[0]?.category, blocking_factor_methodology: resp.data[0]?.category_blocking_factor }
                        resolve(config.value)
                    }
                },
                (error: any) => {
                    console.error('Error fetching experiment methodology:', error)
                    reject(error)
                },
            )
        })
    }

    const getDMLCardDetails = async (literal: string, group_name: string, kpiname: string, methodology: string) => {
        return new Promise((resolve) => {
            const params = {
                literal,
                group_name,
                kpiname,
                methodology,
                measurement_start_date: null,
                measurement_end_date: null,
                is_slice_and_dice: false,
                mtegrouptext: group_name ?? '',
            }
            getDMLCardData(
                params,
                (resp: any) => {
                    resolve(resp.data)
                },
                () => {
                    resolve(null)
                },
            )
        })
    }

    const getMTECardDetails = async (literal: string, group_name: string, kpiname: string, methodology: string) => {
        return new Promise((resolve) => {
            const params = {
                literal,
                group_name,
                kpiname,
                methodology,
                measurement_start_date: null,
                measurement_end_date: null,
                is_slice_and_dice: false,
            }
            getMTECardData(
                params,
                (resp: any) => {
                    resolve(resp.data)
                },
                () => {
                    resolve(null)
                },
            )
        })
    }
    const getCardDetails = async (literal: string, group_name: string, kpiname: string, methodology: string) => {
        return new Promise((resolve) => {
            const params = {
                literal,
                group_name,
                kpiname,
                methodology,
                measurement_start_date: null,
                measurement_end_date: null,
                is_slice_and_dice: false,
            }
            getCardData(
                params,
                (resp: any) => {
                    resolve(resp.data)
                },
                (error: any) => {
                    resolve(null)
                },
            )
        })
    }

    const getDataBeforeLoad = async (experiment: any): Promise<any> => {
        const exp = { ...experiment, insight: {} as any }

        if (exp?.experiment_metadatas?.interventions[0]?.group_configs) {
            const groups = exp?.experiment_metadatas?.interventions[0]?.group_configs.filter((data: any) => !data['is_control']).sort((a: any, b: any) => a.name.localeCompare(b.name))
            exp.insight.groups = groups
        }

        const kpis = exp?.experiment_metadatas?.metrices.map((metric: any) => {
            return {
                label: metric?.literral_value,
                value: metric?.literral_value,
                kpi_type: metric?.kpi_type,
                ...metric,
            }
        })
        exp.insight.kpis = kpis

        if (exp.insight.groups) {
            exp.insight.kpiCombinations = []
            exp.insight.groups.forEach((group: any) => {
                exp.insight.kpis.forEach((kpi: any) => {
                    exp.insight.kpiCombinations.push({
                        group: group?.name,
                        kpi: kpi?.label,
                    })
                })
            })
        } else {
            exp.insight.kpiCombinations = exp.insight.kpis.map((kpi: any) => {
                return {
                    kpi: kpi?.label,
                }
            })
        }
        try {
            const is_mte = exp?.experiment_type?.title == EXPERIMENT_TYPES.MTE.title
            const is_dml = exp?.experiment_type?.title == EXPERIMENT_TYPES.DML.title
            const methodology: any = await getMethodologyByKPI(exp.experiment_literal, exp.insight.kpiCombinations[0]?.group, exp.insight.kpiCombinations[0]?.kpi, is_mte)
            exp.insight.kpiCombinations[0].methodology = methodology

            if (is_dml) {
                exp.insight.kpiCombinations[0].cardDetails = null
                exp.insight.kpiCombinations[0].mteCardDetails = null
                const dmlCardData = await getDMLCardDetails(exp.experiment_literal, exp.insight.kpiCombinations[0]?.group, exp.insight.kpiCombinations[0]?.kpi, methodology)
                exp.insight.kpiCombinations[0].dmlCardDetails = dmlCardData
                console.log('DML Card Data:', dmlCardData)
            } else if (is_mte) {
                exp.insight.kpiCombinations[0].cardDetails = null
                exp.insight.kpiCombinations[0].dmlCardDetails = null
                const mteCardData = await getMTECardDetails(exp.experiment_literal, exp.insight.kpiCombinations[0]?.group, exp.insight.kpiCombinations[0]?.kpi, methodology)
                exp.insight.kpiCombinations[0].mteCardDetails = mteCardData
                console.log('MTE Card Data:', mteCardData)
            } else {
                const cardDetails: any = await getCardDetails(exp.experiment_literal, exp.insight.kpiCombinations[0]?.group, exp.insight.kpiCombinations[0]?.kpi, methodology)
                const UNIT = exp.insight.kpis.find((k: any) => k.label === exp.insight.kpiCombinations[0].kpi)?.kpi_unit || ''
                exp.insight.kpiCombinations[0].cardDetails = { ...cardDetails, kpi_unit: UNIT }
                exp.insight.kpiCombinations[0].mteCardDetails = null
                exp.insight.kpiCombinations[0].dmlCardDetails = null
                console.log('Card Data:', cardDetails)
            }
        } catch (error: any) {
            console.error('Error loading methodology/card details for', exp.experiment_literal, error)
            exp.insight.kpiCombinations[0].methodology = null
            exp.insight.kpiCombinations[0].cardDetails = null
            exp.insight.kpiCombinations[0].mteCardDetails = null
        }

        return exp
    }

    const preloadData = async (experiments: any) => {
        setExperimentDetailLoadingStatus('Arrange data and fetching insights for all experiments')
        const experimentDatas: any = []
        Object.keys(experiments).forEach((key) => {
            if (key !== 'childs') {
                experimentDatas.push(experiments[key])
            }
        })
        Object.keys(experiments.childs).forEach((key) => {
            experimentDatas.push(experiments.childs[key])
        })
        const results = await Promise.all(experimentDatas.map((experiment: any) => getDataBeforeLoad(experiment)))
        const newExperiments: any = { childs: {} }
        results.forEach((experiment: any) => {
            if (experiments[experiment.experiment_literal]) {
                newExperiments[experiment.experiment_literal] = experiment
            } else {
                newExperiments.childs[experiment.experiment_literal] = experiment
            }
        })
        setExperiments(newExperiments)
        setmteCardData(newExperiments[activeExperimentLiteral || experimentHitLiteral]?.insight?.kpiCombinations[0]?.mteCardDetails)
        setdmlCardData(newExperiments[activeExperimentLiteral || experimentHitLiteral]?.insight?.kpiCombinations[0]?.dmlCardDetails)
        setActiveExperimentLiteral(experimentHitLiteral)
        setLoading(false)
    }

    const updateExperiments = (experiment: any) => {
        if (experiments[experiment.experiment_literal]) {
            setExperiments({ ...experiments, [experiment.experiment_literal]: experiment })
            return
        }

        if (experiments.childs[experiment.experiment_literal]) {
            const childs = experiments.childs
            childs[experiment.experiment_literal] = experiment
            setExperiments({ ...experiments, childs })
        }
    }

    useEffect(() => {
        void getExperimentALLData(experimentHitLiteral)
    }, [])

    useEffect(() => {
        if (experiment) {
            trackEvent('insight_page_viewed', {
                experiment_literal: experiment.experiment_literal,
                is_recurring_child: !!experiment.is_recurring_child_experiment,
                has_schedule_parent: !!experiment.schedule_parent_experiment,
            })
        }
    }, [experiment])

    return (
        <DashboardLayout active="my-experiment">
            <Container className="relative p-2">
                {experiments && parentExperimentLiteral && !loading && (
                    <div className="font-sans">
                        <ExperimentHeaderBar
                            child={false}
                            experiment={experiments[parentExperimentLiteral]}
                            setExperiments={setExperiments}
                            activeExperimentLiteral={activeExperimentLiteral}
                            showDetailsToggle={(literal) => setActiveExperimentLiteral(activeExperimentLiteral === literal ? null : literal)}
                            mteCardData={mteCardData}
                            dmlCardData={dmlCardData}
                        />

                        {experiments.childs && Object.keys(experiments.childs).length > 0 && (
                            <div className="ml-6 pl-4 space-y-2 pt-2 relative before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-[1.27rem] before:w-0.5 before:bg-slate-300">
                                {Object.keys(experiments.childs).map((key) => (
                                    <ExperimentHeaderBar
                                        child={true}
                                        key={key}
                                        experiment={experiments.childs[key]}
                                        setExperiments={updateExperiments}
                                        activeExperimentLiteral={activeExperimentLiteral}
                                        showDetailsToggle={(literal) => setActiveExperimentLiteral(activeExperimentLiteral === literal ? null : literal)}
                                        mteCardData={mteCardData}
                                        dmlCardData={dmlCardData}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {!loading && !experiments && experiment && <ExperimentDetails experiment={experiment} setExperiment={setExperiment} mteCardData={mteCardData} dmlCardData={dmlCardData} />}

                {loading && (
                    <div className="flex flex-col items-center justify-center w-full min-h-80 font-sans">
                        <Loader />
                        <p className="text-[0.7rem]">{t(experimentDetailLoadingStatus)}</p>
                    </div>
                )}
            </Container>
        </DashboardLayout>
    )
}

export default Insight
